import React, { FC, useCallback, useEffect, useReducer, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { Store } from '@/redux';
import {
  adMob,
  AdPlatform,
  InterstitialEvent,
  InterstitialResult,
  initialPlayGate,
  isGateBusy,
  playGateReducer,
  PlayGateState,
  playRequirement,
  PlayRequirement,
} from '@/shared/lib';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { useCoinWallet } from './use-coin-wallet';
import { coinWalletQueryKey } from './coin-wallet';

/** Ad runner seam so tests never touch the native plugin. */
export interface AdRunner {
  readonly platform: AdPlatform;
  readonly showInterstitialForPlay: (
    onEvent?: (e: InterstitialEvent) => void
  ) => Promise<InterstitialResult>;
}

export interface UseAdGatedPlay {
  readonly state: PlayGateState;
  readonly requirement: PlayRequirement;
  readonly eligibilityLoading: boolean;
  readonly eligibilityError: boolean;
  /** Runs `action` now, or after the ad flow when an ad is required. */
  readonly requestPlay: (action: () => void) => void;
  /** Call once the play attempt succeeded (entry accepted by the server). */
  readonly markPlayed: () => void;
  readonly watchAd: () => void;
  readonly skipAfterFailure: () => void;
  readonly cancel: () => void;
}

export const useAdGatedPlay = (runner: AdRunner = adMob): UseAdGatedPlay => {
  const {
    wallet,
    isLoading: eligibilityLoading,
    isError: eligibilityError,
    refetch,
  } = useCoinWallet();
  const userId = useSelector((s: Store) => s.userReducer.id);
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(playGateReducer, initialPlayGate);
  const pending = useRef<(() => void) | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const requirement = playRequirement({
    platform: runner.platform,
    adFreeMatchAvailable: wallet?.adFreeMatchAvailable,
  });

  const requestPlay = useCallback(
    (action: () => void) => {
      pending.current = action;
      if (eligibilityLoading) return;
      if (eligibilityError || !wallet) {
        refetch();
        return;
      }
      dispatch({ type: 'request', requirement });
    },
    [eligibilityLoading, eligibilityError, wallet, refetch, requirement]
  );

  useEffect(() => {
    if (state.kind !== 'go') return;
    dispatch({ type: 'started' });
    const run = pending.current;
    pending.current = null;
    run?.();
  }, [state]);

  const watchAd = useCallback(() => {
    dispatch({ type: 'watch' });
    runner
      .showInterstitialForPlay(e => {
        if (e === 'shown' && mounted.current) dispatch({ type: 'shown' });
      })
      .then(result => {
        if (mounted.current) dispatch({ type: 'result', result });
      });
  }, [runner]);

  const markPlayed = useCallback(() => {
    dispatch({ type: 'played' });
    pending.current = null;
    // matchesEntered / adFreeMatchAvailable changed server-side.
    if (userId) queryClient.invalidateQueries(coinWalletQueryKey(userId));
  }, [queryClient, userId]);

  useEffect(() => {
    if (!pending.current || eligibilityLoading || eligibilityError || !wallet)
      return;
    dispatch({ type: 'request', requirement });
  }, [eligibilityLoading, eligibilityError, wallet, requirement]);

  return {
    state,
    requirement,
    eligibilityLoading,
    eligibilityError,
    requestPlay,
    markPlayed,
    watchAd,
    skipAfterFailure: useCallback(
      () => dispatch({ type: 'skipAfterFailure' }),
      []
    ),
    cancel: useCallback(() => {
      pending.current = null;
      dispatch({ type: 'cancel' });
    }, []),
  };
};

const FAILURE_COPY: Record<string, string> = {
  unavailable: "Ads aren't available on this device right now.",
  consent: "Ads can't be requested with your current privacy choices.",
  load: "The ad didn't load. This happens offline or when no ad is available.",
  show: "The ad couldn't finish playing.",
};

export interface AdPlayGateDialogProps {
  readonly gate: UseAdGatedPlay;
  readonly entryFee?: number;
}

/** Explains the ad before it plays, shows progress and failure choices. */
export const AdPlayGateDialog: FC<AdPlayGateDialogProps> = ({
  gate,
  entryFee,
}) => {
  const { state, watchAd, skipAfterFailure, cancel } = gate;
  const open =
    state.kind === 'explain' || state.kind === 'failed' || isGateBusy(state);
  const busy = isGateBusy(state);

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && !busy) cancel();
      }}
    >
      <DialogContent
        showCloseButton={!busy}
        onEscapeKeyDown={e => busy && e.preventDefault()}
        onPointerDownOutside={e => busy && e.preventDefault()}
        aria-busy={busy}
        data-testid="ad-play-gate"
      >
        {state.kind === 'explain' && (
          <>
            <DialogHeader>
              <DialogTitle>One short ad, then you&apos;re dealt in</DialogTitle>
              <DialogDescription>
                Your first account match was ad-free. Each new table after that
                is supported by one short full-screen ad. It plays once, right
                now, and you can close it when it ends.
                {entryFee
                  ? ` The usual ${entryFee}-coin entry still applies.`
                  : ''}
              </DialogDescription>
            </DialogHeader>
            <p className="bh-small bh-muted" style={{ margin: 0 }}>
              Your device may first ask about ad privacy or tracking. Any choice
              works — it only changes how ads are personalised.
            </p>
            <DialogFooter>
              <button
                type="button"
                className="bh-btn bh-btn--ghost bh-btn--small"
                onClick={cancel}
              >
                Not now
              </button>
              <button
                type="button"
                className="bh-btn bh-btn--primary bh-btn--small"
                onClick={watchAd}
                autoFocus
              >
                Watch ad &amp; play
              </button>
            </DialogFooter>
          </>
        )}

        {busy && (
          <DialogHeader>
            <DialogTitle>
              {state.kind === 'showing' ? 'Ad playing…' : 'Loading the ad…'}
            </DialogTitle>
            <DialogDescription aria-live="polite">
              Your table is dealt as soon as the ad closes.
            </DialogDescription>
          </DialogHeader>
        )}

        {state.kind === 'failed' && (
          <>
            <DialogHeader>
              <DialogTitle>No ad this time</DialogTitle>
              <DialogDescription role="alert">
                {FAILURE_COPY[state.stage] || FAILURE_COPY.load} You can try
                again or play this table without an ad.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <button
                type="button"
                className="bh-btn bh-btn--ghost bh-btn--small"
                onClick={cancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="bh-btn bh-btn--secondary bh-btn--small"
                onClick={skipAfterFailure}
              >
                Play without ad
              </button>
              <button
                type="button"
                className="bh-btn bh-btn--primary bh-btn--small"
                onClick={watchAd}
                autoFocus
              >
                Try again
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
